import React from 'react';

import type { AlertProps, TypographyProps } from '@mui/material';
import { Alert, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { filter, includes, isEmpty, isNil, map, orderBy } from 'lodash';
import { createScopedI18n } from '../i18n/i18n';
import { formatDate } from '../utils/libs';
import type { Cashback } from './use_restaurant_cashbacks';

export type CashbackInfoProps = {
  cashbacks?: Cashback[] | null;
  restaurantIds?: string[];
  alertProps?: React.ComponentProps<typeof Alert>;
  typographyProps?: TypographyProps;
};

const cashbackInfoI18n = createScopedI18n('components.cashback_info', { joinOutput: true });

export const filterAvailableCashbacks = (cashbacks?: Cashback[] | null, restaurantIds?: string[]): Cashback[] => {
  let filteredCashbacks = filter(
    cashbacks,
    (cashback) => isNil(cashback.usedAt) && (isNil(cashback.expiredAt) || dayjs(cashback.expiredAt).isAfter(dayjs())),
  );

  if (!isEmpty(restaurantIds)) {
    filteredCashbacks = filter(filteredCashbacks, (cashback) => includes(restaurantIds, cashback.restaurant?.id));
  }

  filteredCashbacks = orderBy(filteredCashbacks, (cashback) =>
    isNil(cashback.expiredAt) ? Number.POSITIVE_INFINITY : dayjs(cashback.expiredAt).valueOf(),
  );

  return filteredCashbacks;
};

export const formatCashbackItem = (cashback: Cashback) => {
  let itemMessage = cashbackInfoI18n('available_cashbacks_item', { count: cashback.amount });

  if (!isNil(cashback.expiredAt)) {
    const expiredAtString = cashbackInfoI18n('available_cashbacks_expired_at', {
      date: formatDate(cashback.expiredAt, 'D MMM YYYY'),
    });
    itemMessage += ` ${expiredAtString}`;
  }

  return itemMessage;
};

const CashbackInfo = ({ cashbacks, restaurantIds, alertProps, typographyProps }: CashbackInfoProps) => {
  const availableCashbacks = React.useMemo(
    () => filterAvailableCashbacks(cashbacks, restaurantIds),
    [cashbacks, restaurantIds],
  );

  if (isEmpty(availableCashbacks)) {
    return null;
  }

  return (
    <Alert
      variant="filled"
      severity="info"
      color={'primary' as AlertProps['color']}
      {...alertProps}
      sx={[
        { '& .MuiAlert-icon': { display: 'none' } },
        ...(Array.isArray(alertProps?.sx) ? alertProps?.sx ?? [] : [alertProps?.sx]),
      ]}
    >
      {map(availableCashbacks, (cashback) => (
        <Typography key={cashback.id} variant="inherit" {...typographyProps}>
          {formatCashbackItem(cashback)}
        </Typography>
      ))}
    </Alert>
  );
};

export default CashbackInfo;
